import compact from "lodash/compact";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { ActivityCustomValuesList } from "PFApp/components/custom_values_list";
import { useTemplate } from "PFApp/hooks";
import { Stack } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { useMatchableTypes } from "PFCore/helpers/activities";
import { propertyToTitle } from "PFCore/helpers/templates";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { AccessLevel } from "PFTypes";
import { useTranslation } from "react-i18next";

export const ActivityOverviewMatchAttributes = (): React.ReactElement | null => {
  const activity = useCurrentActivity();
  const { t } = useTranslation("activities", { keyPrefix: "show.details.matchAttributes" });
  const matchableTypes = useMatchableTypes({
    templateId: activity.template_id,
    levels: [AccessLevel.ReadWrite, AccessLevel.ReadOnly],
    activityCreator: activity.profile
  });
  const template = useTemplate(activity.template_id);
  const { customTypes } = useCustomTypes();

  const customFields = compact(
    // We iterate matchableTypes to keep the order specified in the template, as they are already sorted
    matchableTypes.map(({ name: matchableTypeName }) => {
      const roleCustomField = activity.custom_fields.find(({ type }) => type.name === matchableTypeName);
      const isPresentInRole = !!roleCustomField;
      const hasValues = (roleCustomField?.values || []).length > 0;

      if (isPresentInRole && hasValues) {
        return roleCustomField;
      }

      return null;
    })
  );

  return customFields.length > 0 ? (
    <Tile variant="light" paddingVariant="lg" data-qa-id="ActivityDetailsMatchAttributes">
      <Stack gap="spacingLg">
        <Typography variant="h5">{t("title")}</Typography>
        {customFields.map((customField) => (
          <Stack key={customField.type.id} qaId={`ActivityDetailsMatchAttribute-${customField.type.name}`}>
            <Typography variant="bodyRegular">
              {propertyToTitle(template, customField.type.name, customTypes)}
            </Typography>
            <ActivityCustomValuesList
              customFieldName={customField.type.name}
              customValues={customField.values}
              activityId={activity.id}
              limit={Infinity}
            />
          </Stack>
        ))}
      </Stack>
    </Tile>
  ) : null;
};
